import { FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  HomeIcon,
  BuildingOffice2Icon,
  StarIcon,
  XMarkIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/solid'
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline'
import { DestinationInterface } from '../../interfaces/Account/DestinationInterface'
import { filterDestinationByQuery } from '../../utils/shipmentImportUtils'
import LoadingIcon from '../LoadingIcon'

interface FavoriteDestinationPickerProps {
  favorites: DestinationInterface[]
  selectedId?: string
  selectedDisplay?: string
  placeholder?: string
  disabled?: boolean
  loading?: boolean
  onSelect: (destination: DestinationInterface) => void
  onClear: () => void
  id?: string
}

const DestinationOption: FC<{ destination: DestinationInterface }> = ({
  destination,
}) => {
  const { account, location, businessUnit, isHomeDelivery } = destination
  return (
    <div className="flex flex-col p-2 hover:bg-main-50 cursor-pointer border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-1 mb-1">
        <StarIcon
          className="w-3.5 h-3.5 text-main-500 flex-shrink-0"
          aria-hidden="true"
        />
        <p className="text-sm font-semibold text-gray-900 truncate flex-1 min-w-0">
          {account?.displayName || account?.accountFullName}
        </p>
        <span
          className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 inline-flex items-center gap-1 ${
            isHomeDelivery
              ? 'bg-green-100 text-green-700'
              : 'bg-blue-100 text-blue-700'
          }`}
        >
          {isHomeDelivery ? (
            <HomeIcon className="w-3 h-3" aria-hidden="true" />
          ) : (
            <BuildingOffice2Icon className="w-3 h-3" aria-hidden="true" />
          )}
          <span className="hidden sm:inline">
            {isHomeDelivery ? 'Domicilio' : 'Oficina'}
          </span>
        </span>
      </div>
      {(account?.abbreviationName || account?.identificationNumber) && (
        <p className="text-xs text-gray-500">
          {account?.abbreviationName}
          {account?.identificationNumber}
        </p>
      )}
      {location?.address && (
        <p className="text-xs text-gray-600 truncate flex items-center gap-1">
          <MapPinIcon
            className="w-3 h-3 text-gray-400 flex-shrink-0"
            aria-hidden="true"
          />
          <span className="truncate">{location.address}</span>
        </p>
      )}
      {businessUnit?.buCode && (
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <BuildingStorefrontIcon
            className="w-3 h-3 text-gray-400 flex-shrink-0"
            aria-hidden="true"
          />
          <span>
            {isHomeDelivery ? 'Tienda asignada' : 'Oficina'}: {businessUnit.buCode}
          </span>
        </p>
      )}
    </div>
  )
}

export const FavoriteDestinationPicker: FC<FavoriteDestinationPickerProps> = ({
  favorites,
  selectedId,
  selectedDisplay,
  placeholder = 'Buscar destinatario favorito por Identificación, Nombre o Teléfono',
  disabled = false,
  loading = false,
  onSelect,
  onClear,
  id,
}) => {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIdx, setHighlightIdx] = useState(0)
  // Track when user just cleared a favorite so the input renders animated
  const [justCleared, setJustCleared] = useState(false)
  // G.2: "change favorite" mode — keeps current selection visible until user picks
  // a new one. Click the ✏ icon on the chip → switch to search view; cancel → revert.
  const [isChangingMode, setIsChangingMode] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const selectedDestination = useMemo(() => {
    if (!selectedId) return undefined
    return favorites.find(
      (f) => (f.id || f.account?.id) === selectedId
    )
  }, [selectedId, favorites])

  // Only run filter when dropdown is open (perf: 50 rows × 200 favs).
  const filtered = useMemo(() => {
    if (!isOpen) return []
    return favorites.filter((d) => filterDestinationByQuery(d, search))
  }, [isOpen, favorites, search])

  // Reset highlight when filter changes
  useEffect(() => {
    if (highlightIdx >= filtered.length) setHighlightIdx(0)
  }, [filtered.length, highlightIdx])

  // Clear "just cleared" flag after the transition runs once
  useEffect(() => {
    if (!justCleared) return
    const t = setTimeout(() => setJustCleared(false), 250)
    return () => clearTimeout(t)
  }, [justCleared])

  // Close on click outside. If in change-mode, also exit change-mode so the user
  // ends up back at the chip view instead of stuck on the banner with no dropdown.
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
        setSearch('')
        if (isChangingMode) setIsChangingMode(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, isChangingMode])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
        setSearch('')
        inputRef.current?.blur()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen])

  const handleSelect = (destination: DestinationInterface) => {
    onSelect(destination)
    setIsOpen(false)
    setSearch('')
    setHighlightIdx(0)
    setIsChangingMode(false) // G.2: exit change mode after picking
  }

  const handleClear = () => {
    onClear()
    setSearch('')
    setIsOpen(false)
    setHighlightIdx(0)
    setJustCleared(true)
    setIsChangingMode(false)
  }

  // G.2: ESC inside change-mode cancels back to chip view without losing selection
  useEffect(() => {
    if (!isChangingMode) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setIsChangingMode(false)
        setSearch('')
        setIsOpen(false)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isChangingMode])

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIdx((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const pick = filtered[highlightIdx]
      if (pick) handleSelect(pick)
    } else if (e.key === 'Home') {
      e.preventDefault()
      setHighlightIdx(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      setHighlightIdx(Math.max(filtered.length - 1, 0))
    }
  }

  // G.2: in "changing" mode, render search/dropdown ABOVE the chip context so
  // the user knows they're picking a replacement (not creating from scratch).
  // Falls through to the standard search view below; selection (handleSelect) exits
  // change mode and applies the new favorite.

  // Selected chip view (only when NOT changing)
  if (selectedId && (selectedDestination || selectedDisplay) && !isChangingMode) {
    const display =
      selectedDestination?.account?.displayName ||
      selectedDestination?.account?.accountFullName ||
      selectedDisplay ||
      ''
    const subText = selectedDestination
      ? [
          selectedDestination.account?.identificationNumber,
          selectedDestination.isHomeDelivery
            ? selectedDestination.location?.address
            : selectedDestination.businessUnit?.buCode,
        ]
          .filter(Boolean)
          .join(' · ')
      : ''
    return (
      <div
        ref={containerRef}
        className="bg-main-50 border border-main-200 rounded-lg p-3 flex items-start gap-2"
      >
        <StarIcon
          className="w-4 h-4 text-main-500 flex-shrink-0 mt-0.5"
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {display}
          </p>
          {subText && (
            <p className="text-xs text-gray-600 truncate">{subText}</p>
          )}
        </div>
        {loading && (
          <div
            className="flex items-center gap-1 text-xs text-gray-500"
            aria-live="polite"
          >
            <LoadingIcon size="1rem" />
            <span className="hidden sm:inline">Aplicando…</span>
          </div>
        )}
        {!loading && !disabled && (
          <>
            {/* G.2: Cambiar favorito — abre el dropdown sin perder la selección actual */}
            <button
              type="button"
              onClick={() => {
                setIsChangingMode(true)
                setIsOpen(true)
                setSearch('')
                // focus input after render
                setTimeout(() => inputRef.current?.focus(), 0)
              }}
              className="text-gray-500 hover:text-main-500 flex-shrink-0 p-1 -m-1 rounded hover:bg-main-100"
              aria-label="Cambiar destinatario favorito"
              title="Cambiar destinatario favorito"
            >
              <PencilSquareIcon className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-500 hover:text-red-600 flex-shrink-0 p-1 -m-1 rounded hover:bg-main-100"
              aria-label="Quitar destinatario favorito"
              title="Quitar destinatario favorito"
            >
              <XMarkIcon className="w-4 h-4" aria-hidden="true" />
            </button>
          </>
        )}
      </div>
    )
  }

  // Search input + dropdown view
  const listboxId = id ? `${id}-listbox` : undefined
  const optionId = (idx: number) =>
    id ? `${id}-option-${idx}` : `fav-option-${idx}`
  return (
    <div ref={containerRef} className="relative transition-all duration-200">
      {/* G.2: when in "changing" mode show a hint banner so user knows current selection persists until they pick */}
      {isChangingMode && selectedId && (
        <div className="mb-2 flex items-center justify-between gap-2 text-xs bg-main-50 border border-main-200 rounded-md px-3 py-1.5">
          <span className="text-gray-700 truncate">
            Cambiando favorito… (Esc para cancelar)
          </span>
          <button
            type="button"
            onClick={() => {
              setIsChangingMode(false)
              setSearch('')
              setIsOpen(false)
            }}
            className="text-gray-500 hover:text-gray-700 text-xs underline flex-shrink-0"
          >
            Cancelar
          </button>
        </div>
      )}
      <div className="relative">
        <MagnifyingGlassIcon
          className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          id={id}
          type="text"
          autoComplete="off"
          value={search}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setSearch(e.target.value)
            if (!isOpen) setIsOpen(true)
          }}
          onKeyDown={handleInputKeyDown}
          className={`w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-main-500 focus:border-main-500 disabled:bg-gray-50 disabled:cursor-not-allowed transition-all duration-200 ${
            justCleared ? 'ring-2 ring-main-200' : ''
          }`}
          aria-label={placeholder}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={
            isOpen && filtered[highlightIdx] ? optionId(highlightIdx) : undefined
          }
          role="combobox"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <LoadingIcon size="1rem" />
          </div>
        )}
      </div>

      {isOpen && (
        <>
          {/* Mobile backdrop: closes picker when tapped outside the sheet */}
          <div
            className="fixed inset-0 z-30 bg-black/30 sm:hidden"
            onClick={() => {
              setIsOpen(false)
              setSearch('')
            }}
            aria-hidden="true"
          />
          <div
            id={listboxId}
            ref={listRef}
            className="fixed inset-x-4 bottom-4 top-20 z-40 bg-white border border-gray-200 rounded-md shadow-xl overflow-y-auto sm:absolute sm:inset-auto sm:bottom-auto sm:top-auto sm:z-20 sm:mt-1 sm:w-full sm:max-h-72"
            role="listbox"
            aria-label="Destinatarios favoritos"
          >
            {/* Mobile-only header to anchor the bottom-sheet */}
            <div className="sticky top-0 flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200 sm:hidden">
              <span className="text-sm font-medium text-gray-700">
                Destinatarios favoritos
              </span>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false)
                  setSearch('')
                }}
                className="p-2 -m-2 text-gray-500 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <XMarkIcon className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
            {favorites.length === 0 ? (
              <p className="p-3 text-sm text-gray-500 italic">
                Sin destinatarios favoritos guardados
              </p>
            ) : filtered.length === 0 ? (
              <p className="p-3 text-sm text-gray-500 italic">
                No se encontraron favoritos con "{search}"
              </p>
            ) : (
              filtered.map((destination, idx) => (
                <div
                  key={destination.id || destination.account?.id || idx}
                  id={optionId(idx)}
                  role="option"
                  aria-selected={idx === highlightIdx}
                  tabIndex={-1}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  onClick={() => handleSelect(destination)}
                  className={
                    idx === highlightIdx
                      ? 'bg-main-50 ring-1 ring-main-200'
                      : ''
                  }
                >
                  <DestinationOption destination={destination} />
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default FavoriteDestinationPicker
